// src/Pages/ReceptionistDashboard/PatientRegistration.js

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  TextField,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider,
  Snackbar,
  Alert,
  Tooltip,
  useMediaQuery,
  useTheme,
  CircularProgress,
  Card,
  CardContent,
  Chip,
  Avatar,
  Stack,
} from '@mui/material';
import {
  PersonAdd as PersonAddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Delete as DeleteIcon,
  CloudUpload as CloudUploadIcon,
  Refresh as RefreshIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
} from '@mui/icons-material';
import axiosInstance from '../../api/axiosInstance';

const PatientRegistration = () => {
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isNewPatientDialogOpen, setIsNewPatientDialogOpen] = useState(false);
  const [notification, setNotification] = useState(null);
  const [newPatientForm, setNewPatientForm] = useState({
    name: '',
    phone: '',
    email: '',
    age: '',
    city: '',
    gender: '',
  });
  
  // Quick create state
  const [quickCreateMode, setQuickCreateMode] = useState(false);
  const [quickCreateForm, setQuickCreateForm] = useState({
    name: '',
    phone: '',
    email: '',
    sendMagicLink: true,
    channel: 'sms' // 'sms', 'email', 'whatsapp'
  });
  const [generatedMagicLink, setGeneratedMagicLink] = useState(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Fetch patients
  const fetchPatients = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/appointments/receptionist/patients/');
      setPatients(response.data.patients || []);
    } catch (error) {
      console.error('Error fetching patients:', error);
      setNotification({
        message: 'Failed to fetch patients',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPatients();
  }, [fetchPatients]);

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleEditPatient = (patient) => {
    setSelectedPatient(patient);
    setIsEditDialogOpen(true);
  };

  // Quick create patient with magic link
  const handleQuickCreate = async () => {
    try {
      setLoading(true);
      
      const response = await axiosInstance.post('/api/v1/auth/quick-create-patient/', {
        name: quickCreateForm.name,
        phone: quickCreateForm.phone,
        email: quickCreateForm.email,
        sendMagicLink: quickCreateForm.sendMagicLink, // Backend supports both camelCase and snake_case
        channel: quickCreateForm.channel
      });
      
      if (response.data.magic_link) {
        setGeneratedMagicLink(response.data.magic_link);
        setNotification({
          message: 'Patient created successfully! Magic link sent.',
          severity: 'success'
        });
      } else {
        setNotification({
          message: 'Patient created successfully!',
          severity: 'success'
        });
      }
      
      // Reset form
      setQuickCreateForm({
        name: '',
        phone: '',
        email: '',
        sendMagicLink: true,
        channel: 'sms'
      });
      setQuickCreateMode(false);
      
      // Refresh patient list
      await fetchPatients();
      
    } catch (error) {
      console.error('Error creating patient:', error);
      const errorMessage = error.response?.data?.error || 
                          error.response?.data?.message || 
                          error.response?.data?.detail || 
                          error.message || 
                          'Failed to create patient. Please try again.';
      setNotification({
        message: errorMessage,
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleNewPatient = () => {
    setNewPatientForm({
      name: '',
      phone: '',
      email: '',
      age: '',
      city: '',
      gender: '',
    });
    setIsNewPatientDialogOpen(true);
  };

  const handleSaveChanges = async () => {
    try {
      // API call to update patient
      // await axios.patch(`/api/v1/patients/${selectedPatient.id}/`, selectedPatient);
      
      setNotification({
        message: 'Patient profile updated successfully',
        severity: 'success',
      });
      setIsEditDialogOpen(false);
      fetchPatients(); // Refresh the list
    } catch (error) {
      const errorMessage = error.response?.data?.error || 
                          error.response?.data?.message || 
                          error.response?.data?.detail || 
                          'Failed to update patient. Please try again.';
      setNotification({
        message: errorMessage,
        severity: 'error',
      });
    }
  };

  const handleAddPatient = async () => {
    try {
      const response = await axiosInstance.post('/api/v1/auth/quick-create-patient/', {
        ...newPatientForm,
        sendMagicLink: true, // Auto-send magic link for new patients
      });
      
      setNotification({
        message: response.data.message || 'New patient registered successfully',
        severity: 'success',
      });
      setIsNewPatientDialogOpen(false);
      setNewPatientForm({
        name: '',
        phone: '',
        email: '',
        age: '',
        city: '',
        gender: '',
      });
      
      // Refresh patient list
      await fetchPatients();
    } catch (error) {
      console.error('Error adding patient:', error);
      const errorMessage = error.response?.data?.error || 
                          error.response?.data?.message || 
                          error.response?.data?.detail || 
                          error.message ||
                          'Failed to register patient. Please try again.';
      setNotification({
        message: errorMessage,
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseNotification = () => {
    setNotification(null);
  };

  const filteredPatients = patients.filter(
    (patient) =>
      patient.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      patient.phone.includes(searchTerm) ||
      patient.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ padding: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant='h4' gutterBottom>
          Patient Registration & Profile Management
        </Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={fetchPatients}
        >
          Refresh
        </Button>
      </Box>

      {/* Search and Add New Patient */}
      <Grid container spacing={2} sx={{ marginBottom: 2 }}>
        <Grid item xs={12} md={8}>
          <TextField
            fullWidth
            label='Search by Name, Phone, or Email'
            variant='outlined'
            value={searchTerm}
            onChange={handleSearchChange}
            InputProps={{
              endAdornment: <SearchIcon />,
            }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <Stack direction="row" spacing={1}>
            <Button
              variant='contained'
              startIcon={<PersonAddIcon />}
              onClick={() => setQuickCreateMode(true)}
              sx={{ flex: 1, height: '56px' }}
            >
              Quick Create
            </Button>
            <Button
              variant='outlined'
              startIcon={<PersonAddIcon />}
              onClick={handleNewPatient}
              sx={{ flex: 1, height: '56px' }}
            >
              Full Registration
            </Button>
          </Stack>
        </Grid>
      </Grid>

      {/* Patient Statistics */}
      <Grid container spacing={2} sx={{ marginBottom: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant='h6' color='primary'>
                Total Patients
              </Typography>
              <Typography variant='h4'>{patients.length}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant='h6' color='success.main'>
                Active Patients
              </Typography>
              <Typography variant='h4'>
                {patients.filter(p => p.status === 'active').length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant='h6' color='info.main'>
                New This Month
              </Typography>
              <Typography variant='h4'>
                {patients.filter(p => new Date(p.registration_date).getMonth() === new Date().getMonth()).length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant='h6' color='warning.main'>
                Pending Updates
              </Typography>
              <Typography variant='h4'>0</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Patient List */}
      {filteredPatients.length === 0 ? (
        <Paper elevation={2} sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            {searchTerm ? 'No patients match your search criteria' : 'No patients registered yet'}
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {filteredPatients.map((patient) => (
            <Grid item xs={12} key={patient.id}>
              <Card elevation={2}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Avatar sx={{ bgcolor: theme.palette.primary.main, width: 40, height: 40 }}>
                          <PersonIcon />
                        </Avatar>
                        <Typography variant="h6">
                          {patient.name}
                        </Typography>
                        <Chip
                          label={patient.status}
                          color={patient.status === 'active' ? 'success' : 'default'}
                          size="small"
                        />
                      </Box>
                      
                      <Stack direction="row" spacing={2} sx={{ mb: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <PhoneIcon fontSize="small" color="action" />
                          <Typography variant="body2" color="text.secondary">
                            {patient.phone}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <EmailIcon fontSize="small" color="action" />
                          <Typography variant="body2" color="text.secondary">
                            {patient.email}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <LocationIcon fontSize="small" color="action" />
                          <Typography variant="body2" color="text.secondary">
                            {patient.city}
                          </Typography>
                        </Box>
                      </Stack>
                      
                      <Typography variant="body2" color="text.secondary">
                        Age: {patient.age} | Gender: {patient.gender} | Registered: {patient.registration_date}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      <Tooltip title="Edit Patient">
                        <IconButton
                          color="primary"
                          onClick={() => handleEditPatient(patient)}
                          size="small"
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      
                      <Tooltip title="View Details">
                        <IconButton color="info" size="small">
                          <PersonIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Edit Patient Dialog */}
      <Dialog open={isEditDialogOpen} onClose={() => setIsEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Patient Profile</DialogTitle>
        <DialogContent>
          {selectedPatient && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Patient Name"
                  value={selectedPatient.name}
                  onChange={(e) => setSelectedPatient({ ...selectedPatient, name: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Phone Number"
                  value={selectedPatient.phone}
                  onChange={(e) => setSelectedPatient({ ...selectedPatient, phone: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email"
                  value={selectedPatient.email}
                  onChange={(e) => setSelectedPatient({ ...selectedPatient, email: e.target.value })}
                  type="email"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Age"
                  value={selectedPatient.age}
                  onChange={(e) => setSelectedPatient({ ...selectedPatient, age: e.target.value })}
                  type="number"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Gender</InputLabel>
                  <Select
                    value={selectedPatient.gender}
                    onChange={(e) => setSelectedPatient({ ...selectedPatient, gender: e.target.value })}
                    label="Gender"
                  >
                    <MenuItem value="Male">Male</MenuItem>
                    <MenuItem value="Female">Female</MenuItem>
                    <MenuItem value="Other">Other</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="City"
                  value={selectedPatient.city}
                  onChange={(e) => setSelectedPatient({ ...selectedPatient, city: e.target.value })}
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveChanges} variant="contained" startIcon={<SaveIcon />}>
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* New Patient Dialog */}
      <Dialog open={isNewPatientDialogOpen} onClose={() => setIsNewPatientDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Register New Patient</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Patient Name"
                value={newPatientForm.name}
                onChange={(e) => setNewPatientForm({ ...newPatientForm, name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone Number"
                value={newPatientForm.phone}
                onChange={(e) => setNewPatientForm({ ...newPatientForm, phone: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                value={newPatientForm.email}
                onChange={(e) => setNewPatientForm({ ...newPatientForm, email: e.target.value })}
                type="email"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Age"
                value={newPatientForm.age}
                onChange={(e) => setNewPatientForm({ ...newPatientForm, age: e.target.value })}
                type="number"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Gender</InputLabel>
                <Select
                  value={newPatientForm.gender}
                  onChange={(e) => setNewPatientForm({ ...newPatientForm, gender: e.target.value })}
                  label="Gender"
                >
                  <MenuItem value="Male">Male</MenuItem>
                  <MenuItem value="Female">Female</MenuItem>
                  <MenuItem value="Other">Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="City"
                value={newPatientForm.city}
                onChange={(e) => setNewPatientForm({ ...newPatientForm, city: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsNewPatientDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleAddPatient}
            variant="contained"
            startIcon={<PersonAddIcon />}
            disabled={!newPatientForm.name || !newPatientForm.phone}
          >
            Register Patient
          </Button>
        </DialogActions>
      </Dialog>

      {/* Quick Create Dialog */}
      <Dialog open={quickCreateMode} onClose={() => setQuickCreateMode(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Quick Create Patient</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Create a patient account and send them a magic link to complete their profile.
          </Typography>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Patient Name"
                value={quickCreateForm.name}
                onChange={(e) => setQuickCreateForm({ ...quickCreateForm, name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone Number"
                value={quickCreateForm.phone}
                onChange={(e) => setQuickCreateForm({ ...quickCreateForm, phone: e.target.value })}
                required
                placeholder="+91XXXXXXXXXX"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email (Optional)"
                value={quickCreateForm.email}
                onChange={(e) => setQuickCreateForm({ ...quickCreateForm, email: e.target.value })}
                type="email"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Send Magic Link Via</InputLabel>
                <Select
                  value={quickCreateForm.channel}
                  onChange={(e) => setQuickCreateForm({ ...quickCreateForm, channel: e.target.value })}
                  label="Send Magic Link Via"
                >
                  <MenuItem value="sms">SMS</MenuItem>
                  <MenuItem value="email">Email</MenuItem>
                  <MenuItem value="whatsapp">WhatsApp</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQuickCreateMode(false)}>Cancel</Button>
          <Button 
            onClick={handleQuickCreate}
            variant="contained"
            startIcon={<PersonAddIcon />}
            disabled={!quickCreateForm.name || !quickCreateForm.phone || loading}
          >
            {loading ? 'Creating...' : 'Create & Send Link'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Magic Link Display Dialog */}
      <Dialog open={Boolean(generatedMagicLink)} onClose={() => setGeneratedMagicLink(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Magic Link Generated</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Patient created successfully! Here's the magic link:
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            value={generatedMagicLink}
            InputProps={{
              readOnly: true,
            }}
            sx={{ mb: 2 }}
          />
          <Typography variant="body2" color="text.secondary">
            You can copy this link and share it with the patient, or it has been sent via {quickCreateForm.channel}.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGeneratedMagicLink(null)}>Close</Button>
          <Button 
            onClick={() => {
              navigator.clipboard.writeText(generatedMagicLink);
              setNotification({
                message: 'Magic link copied to clipboard!',
                severity: 'success'
              });
            }}
            variant="outlined"
          >
            Copy Link
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notification Snackbar */}
      <Snackbar
        open={Boolean(notification)}
        autoHideDuration={4000}
        onClose={handleCloseNotification}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification?.severity}
          sx={{ width: '100%' }}
        >
          {notification?.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PatientRegistration;
